import React, { useState, useEffect } from 'react';
import { navigationManager } from '../utils/navigationManager';
import { 
  Activity,
  Calendar,
  Utensils,
  AlertCircle,
  Heart,
  Thermometer,
  Zap,
  Smartphone,
  Brain,
  Pill,
  TrendingUp,
  Plus,
  Database,
  Save,
  MessageCircle,
  Coins,
  CheckCircle,
  Settings
} from 'lucide-react';
import { StatCard } from './StatCard';
import { QuickActions } from './QuickActions';
import { RecentActivity } from './RecentActivity';
import { HealthMetrics } from './HealthMetrics';
import ComingSoonBanner from './common/ComingSoonBanner';
import { useMedicalConditions } from '../hooks/useSupabaseData';
import { useSymptoms } from '../hooks/useSupabaseData';
import { useMedications } from '../hooks/useSupabaseData';
import { useFoodEntries } from '../hooks/useSupabaseData';
import { useDeviceData } from '../hooks/useSupabaseData';
import ZharaChat from './ai/ZharaChat';
import ZippyCoinWallet from './wallet/ZippyCoinWallet';
import { populateUSDAFoodsSample } from '../lib/supabase';
import { ProductionReadyBanner } from './common/ProductionReadyBanner';
import { EmptyStateWithSampleData } from './common/EmptyStateWithSampleData';
import { useSampleData } from '../hooks/useSampleData';
import { NewsFeed } from './NewsFeed';
import { NewsSubscriptionManager } from './NewsSubscriptionManager';
import { SafeAIInsightsButton } from './SafeAIInsightsButton';
import DataMetricsDisplay from './DataMetricsDisplay';
import MockDataBanner from './common/MockDataBanner';
import { useMockDataDetection } from '../hooks/useMockDataDetection';
import { useOnboardingGuidance } from '../hooks/useMockDataDetection';
import { MVPLaunchBanner } from './common/MVPLaunchBanner';

class DashboardErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('Dashboard error:', error, info); }
  render() {
    if (this.state.hasError) return <div role="alert">Something went wrong in Dashboard.</div>;
    return this.props.children;
  }
}

const Dashboard: React.FC = () => {
  const { conditions } = useMedicalConditions();
  const { symptoms } = useSymptoms();
  const { medications } = useMedications();
  const { foodEntries } = useFoodEntries();
  const { deviceData } = useDeviceData();
  const { shouldShowSampleData } = useSampleData();
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<string>('');
  const [showZhara, setShowZhara] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [dbInitialized, setDbInitialized] = useState(false);
  const [initializingDb, setInitializingDb] = useState(false);
  const [showNewsFeed, setShowNewsFeed] = useState(false);
  const [userConditions, setUserConditions] = useState<string[]>(['diabetes-type-2', 'hypertension']);

  const mockDataInfo = useMockDataDetection('dashboard');
  const { showOnboardingPrompt, dismissOnboardingPrompt, startOnboarding } = useOnboardingGuidance();

  // Check if user has any real data
  const hasUserData = conditions.length > 3 || symptoms.length > 2 || medications.length > 2;
  const showQuickStartGuide = !hasUserData && !localStorage.getItem('zippyhealth_quickstart_dismissed');

  useEffect(() => {
    // Update last save time when data changes
    const updateSaveTime = () => {
      setLastSaveTime(new Date().toLocaleTimeString());
    };
    updateSaveTime();
  }, [conditions, symptoms, medications, foodEntries]);

  useEffect(() => {
    if (showQuickStartGuide) {
      setShowQuickStart(true);
    }
  }, [showQuickStartGuide]);

  const dismissQuickStart = () => {
    setShowQuickStart(false);
    localStorage.setItem('zippyhealth_quickstart_dismissed', 'true');
  };

  // Initialize database with sample data only when sample data is enabled
  useEffect(() => {
    const initializeDatabase = async () => {
      try {
        setInitializingDb(true);
        const result = await populateUSDAFoodsSample();
        if (result.success) {
          setDbInitialized(true);
          console.log(`✅ Database initialized with ${result.count} sample foods`);
        }
      } catch (error) {
        console.error('Database initialization error:', error);
      } finally {
        setInitializingDb(false);
      }
    };

    // Only run if sample data is enabled and not already initialized
    if (shouldShowSampleData('health-metrics') && !dbInitialized && !initializingDb) {
      initializeDatabase();
    }
  }, [dbInitialized, initializingDb, shouldShowSampleData]);

  const handleStartOnboarding = () => {
    startOnboarding();
  };

  const handleAddData = (dataType: string) => {
    switch (dataType) {
      case 'health-conditions':
        window.dispatchEvent(new CustomEvent('showAddCondition'));
        break;
      case 'medications':
        window.location.href = '/medications';
        break;
      case 'symptoms':
        window.location.href = '/symptoms';
        break;
      case 'food':
        window.location.href = '/food-tracking';
        break;
      default:
        break;
    }
  };

  return (
    <DashboardErrorBoundary>
      <div className="space-y-6">
        {/* MVP Launch Banner */}
        <MVPLaunchBanner />
        
        {/* Welcome Section */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Welcome to ZippyHealth MVP
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Track your health data with our simplified, reliable features.
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <Heart className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Conditions</span>
              </div>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{conditions.length}</p>
            </div>
            
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <Thermometer className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Symptoms</span>
              </div>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">{symptoms.length}</p>
            </div>
            
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <Pill className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Medications</span>
              </div>
              <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{medications.length}</p>
            </div>
            
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <Utensils className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Food Entries</span>
              </div>
              <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">{foodEntries.length}</p>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={() => window.location.href = '/conditions'}
              className="flex items-center space-x-3 p-4 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
            >
              <Plus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Add Condition</span>
            </button>
            
            <button 
              onClick={() => window.location.href = '/symptoms'}
              className="flex items-center space-x-3 p-4 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
            >
              <Plus className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Track Symptom</span>
            </button>
            
            <button 
              onClick={() => window.location.href = '/enhanced-food-tracker'}
              className="flex items-center space-x-3 p-4 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
            >
              <Plus className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Log Food</span>
            </button>
            
            <button 
              onClick={() => window.location.href = '/medications'}
              className="flex items-center space-x-3 p-4 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
            >
              <Plus className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Add Medication</span>
            </button>
          </div>
        </div>
        
        {/* Last Updated */}
        {lastSaveTime && (
          <div className="text-xs text-slate-500 dark:text-slate-400 text-center">
            Last updated: {lastSaveTime}
          </div>
        )}
      </div>
    </DashboardErrorBoundary>
  );
};

export default Dashboard;
